/**
 * Centraliserad rollmappning för workspace-navigering.
 * Definierar vilka sektioner och sub-tabs varje roll har tillgång till.
 */

const SECTIONS = {
  PIPELINE: 'pipeline',
  VECKA: 'vecka',
  MONDAY: 'monday',
  UPPHANDLING: 'upphandling',
  PROJEKT: 'projekt',
  PROJEKTERING: 'projektering',
  PRODUKTION: 'produktion',
  DOKUMENT: 'dokument',
  MINA_PU: 'mina-pu',
};

const SUB_TABS = {
  // Projektering
  KANBAN: 'kanban',
  BRIEF: 'brief',
  ATT_GORA: 'attgora',
  PRODUKTVAL: 'produktval',
  RUMSA: 'rumsa',
  TIDPLAN: 'tidplan',
  KUNDINFO: 'kundinfo',
  UNDERLAG: 'underlag',
  AVTAL: 'avtal',
  DOKUMENTATION: 'dokumentation',
  // Ingen route, utan en dropdown i flikraden som samlar estimat, kalkyl, PU,
  // offert och avtal. Innehållet kommer från getAvtalGruppForRole.
  AVTAL_GRUPP: 'avtal-grupp',
  // Möten och ÄTA ligger i Projektering för BK, IA och admin, och i Produktion
  // för byggaren. Protokoll har ingen egen flik — det nås via mötet.
  ATA: 'ata',
  MOTEN: 'moten',
  BEHORIGHETER: 'behorigheter',
};

/**
 * Returnerar vilka huvudsektioner en roll har tillgång till.
 * @param {string} role - Användarens roll
 * @returns {string[]} Array av section-nycklar
 */
export function getSectionsForRole(role) {
  // BK, IA och admin har ingen Produktion-sektion: Möten och ÄTA ligger i
  // Projektering. Byggaren har kvar /workspace/produktion via projektkorten.
  switch (role) {
    case 'byggkonsult':
      return [SECTIONS.PIPELINE, SECTIONS.VECKA, SECTIONS.MONDAY, SECTIONS.UPPHANDLING, SECTIONS.PROJEKT, SECTIONS.PROJEKTERING];
    case 'inredningsarkitekt':
      return [SECTIONS.PIPELINE, SECTIONS.VECKA, SECTIONS.MONDAY, SECTIONS.PROJEKT, SECTIONS.PROJEKTERING];
    case 'byggare':
      // Byggare har bara Översikt + Vecka i top-navet. Övriga vyer
      // (projektering, produktion, dokument, mina PU) når de via
      // projekt-korten på Översikt och knapparna där.
      return [SECTIONS.PIPELINE, SECTIONS.VECKA];
    case 'UE':
      return [SECTIONS.PIPELINE, SECTIONS.PROJEKT, SECTIONS.PROJEKTERING];
    case 'projektor':
      // Projektör har en förenklad vy - endast översikt och underlag
      return [SECTIONS.PIPELINE];
    case 'admin':
      return [SECTIONS.PIPELINE, SECTIONS.VECKA, SECTIONS.MONDAY, SECTIONS.UPPHANDLING, SECTIONS.PROJEKT, SECTIONS.PROJEKTERING];
    default:
      return [SECTIONS.PIPELINE];
  }
}

/**
 * Returnerar vilka sub-tabs en roll har tillgång till för en given sektion.
 * @param {string} role - Användarens roll
 * @param {string} section - Sektionen (projektering/produktion)
 * @returns {string[]} Array av sub-tab-nycklar
 */
export function getSubTabsForRole(role, section) {
  if (section === SECTIONS.PROJEKTERING) {
    switch (role) {
      case 'byggkonsult':
        return [SUB_TABS.KANBAN, SUB_TABS.PRODUKTVAL, SUB_TABS.RUMSA, SUB_TABS.TIDPLAN, SUB_TABS.KUNDINFO,
          SUB_TABS.UNDERLAG, SUB_TABS.AVTAL_GRUPP, SUB_TABS.MOTEN, SUB_TABS.ATA, SUB_TABS.DOKUMENTATION];
      case 'inredningsarkitekt':
        // Utan Kundinfo — den hör till byggkonsultens arbete, inte IA:s.
        // ÄTA är läsvy för IA — ATATab ger bara admin, byggkonsult och byggare
        // rätt att skapa och ändra.
        return [SUB_TABS.KANBAN, SUB_TABS.BRIEF, SUB_TABS.ATT_GORA, SUB_TABS.PRODUKTVAL, SUB_TABS.RUMSA, SUB_TABS.TIDPLAN,
          SUB_TABS.UNDERLAG, SUB_TABS.AVTAL_GRUPP, SUB_TABS.MOTEN, SUB_TABS.ATA];
      case 'byggare':
        return [SUB_TABS.KANBAN, SUB_TABS.PRODUKTVAL, SUB_TABS.RUMSA, SUB_TABS.TIDPLAN];
      case 'UE':
        // ÄTA: bara UE:ns egna, via /api/ata/ue (UeAtaTab) — aldrig projektets
        return [SUB_TABS.PRODUKTVAL, SUB_TABS.RUMSA, SUB_TABS.TIDPLAN, SUB_TABS.ATA];
      case 'projektor':
        // Projektör har inte tillgång till projektering - de har egen förenklad vy
        return [];
      case 'admin':
        return [SUB_TABS.KANBAN, SUB_TABS.BRIEF, SUB_TABS.ATT_GORA, SUB_TABS.PRODUKTVAL, SUB_TABS.RUMSA, SUB_TABS.TIDPLAN, SUB_TABS.KUNDINFO,
          SUB_TABS.UNDERLAG, SUB_TABS.AVTAL_GRUPP, SUB_TABS.MOTEN, SUB_TABS.ATA, SUB_TABS.DOKUMENTATION];
      default:
        return [];
    }
  }

  // Bara byggaren har Produktion kvar. ProduktionSection skickar BK, IA och
  // admin vidare till Projektering om de hamnar här via en gammal länk.
  if (section === SECTIONS.PRODUKTION) {
    switch (role) {
      case 'byggare':
        return [SUB_TABS.MOTEN, SUB_TABS.ATA, SUB_TABS.BEHORIGHETER];
      default:
        return [];
    }
  }

  return [];
}

/**
 * Kontrollerar om en roll har tillgång till en viss sektion.
 * @param {string} role - Användarens roll
 * @param {string} section - Sektionen att kontrollera
 * @returns {boolean}
 */
export function canAccessSection(role, section) {
  return getSectionsForRole(role).includes(section);
}

/**
 * Kontrollerar om en roll har tillgång till en viss sub-tab.
 * @param {string} role - Användarens roll
 * @param {string} section - Sektionen
 * @param {string} subTab - Sub-taben att kontrollera
 * @returns {boolean}
 */
export function canAccessSubTab(role, section, subTab) {
  return getSubTabsForRole(role, section).includes(subTab);
}

/**
 * Returnerar default sub-tab för en roll och sektion.
 * @param {string} role - Användarens roll
 * @param {string} section - Sektionen
 * @returns {string|null}
 */
export function getDefaultSubTab(role, section) {
  const tabs = getSubTabsForRole(role, section);
  // AVTAL_GRUPP är en dropdown, inte en route — den får aldrig bli landningssida.
  // Ligger den inte först i dag, men filtret gör en framtida omordning ofarlig.
  const navigerbara = tabs.filter(t => t !== SUB_TABS.AVTAL_GRUPP);
  return navigerbara.length > 0 ? navigerbara[0] : null;
}

/**
 * Returnerar vilka dokument-typer en roll kan se i dropdown.
 * @param {string} role - Användarens roll
 * @returns {string[]}
 */
export function getDokumentTypesForRole(role) {
  switch (role) {
    case 'byggkonsult':
      return ['estimat', 'kalkyl', 'pu', 'offert', 'underlag', 'avtal', 'dokumentation'];
    case 'inredningsarkitekt':
      // IA har ingen anledning att se avtalet — bara offerten.
      return ['offert', 'underlag'];
    case 'byggare':
      return ['underlag', 'avtal', 'dokumentation'];
    case 'UE':
      return ['underlag'];
    case 'projektor':
      return ['underlag'];
    case 'admin':
      return ['estimat', 'kalkyl', 'pu', 'offert', 'underlag', 'avtal', 'dokumentation'];
    default:
      return [];
  }
}

// Posterna i Avtal-dropdownen, i visningsordning.
const AVTAL_GRUPP_POSTER = ['estimat', 'kalkyl', 'pu', 'offert', 'avtal'];

/**
 * Returnerar innehållet i Avtal-dropdownen för en roll.
 *
 * Härleds ur getDokumentTypesForRole i stället för en egen lista, så att
 * rollskillnaderna bara finns på ett ställe. Byggkonsult och admin får alla
 * fem, inredningsarkitekt bara offert och avtal.
 *
 * @param {string} role
 * @returns {string[]}
 */
export function getAvtalGruppForRole(role) {
  const tillatna = getDokumentTypesForRole(role);
  return AVTAL_GRUPP_POSTER.filter(post => tillatna.includes(post));
}

/**
 * Returnerar display-namn för en sub-tab.
 * @param {string} subTab - Sub-tab-nyckel
 * @param {string} [role] - Användarens roll (valfritt, för rollspecifika namn)
 * @returns {string}
 */
export function getSubTabDisplayName(subTab, role) {
  // Byggare ser Kanban som "Att göra"
  if (subTab === SUB_TABS.KANBAN && role === 'byggare') {
    return 'Att göra';
  }
  const names = {
    [SUB_TABS.KANBAN]: 'Kanban',
    [SUB_TABS.BRIEF]: 'Brief',
    [SUB_TABS.ATT_GORA]: 'Att göra',
    [SUB_TABS.PRODUKTVAL]: 'Produktval',
    [SUB_TABS.RUMSA]: 'Rumsbeskrivning',
    [SUB_TABS.TIDPLAN]: 'Tidplan',
    [SUB_TABS.KUNDINFO]: 'Kundinfo',
    [SUB_TABS.UNDERLAG]: 'Underlag',
    [SUB_TABS.AVTAL]: 'Avtal',
    [SUB_TABS.DOKUMENTATION]: 'Dokumentation',
    [SUB_TABS.AVTAL_GRUPP]: 'Avtal',
    [SUB_TABS.ATA]: 'ÄTA',
    [SUB_TABS.MOTEN]: 'Möten',
    [SUB_TABS.BEHORIGHETER]: 'Behörigheter',
  };
  return names[subTab] || subTab;
}

/**
 * Returnerar display-namn för en sektion.
 * @param {string} section - Sektions-nyckel
 * @returns {string}
 */
export function getSectionDisplayName(section) {
  const names = {
    [SECTIONS.PIPELINE]: 'Översikt',
    [SECTIONS.VECKA]: 'Vecka',
    [SECTIONS.MONDAY]: 'Monday',
    [SECTIONS.UPPHANDLING]: 'Upphandling',
    [SECTIONS.PROJEKT]: 'Projekt',
    [SECTIONS.PROJEKTERING]: 'Projektering',
    [SECTIONS.PRODUKTION]: 'Produktion',
    [SECTIONS.DOKUMENT]: 'Dokument',
    [SECTIONS.MINA_PU]: 'Mina PU',
  };
  return names[section] || section;
}

/**
 * Kontrollerar om en roll har tillgång till upphandlingssektionen.
 * Används för att filtrera pipeline-kolumner.
 * @param {string} role - Användarens roll
 * @returns {boolean}
 */
export function canAccessUpphandling(role) {
  return ['byggkonsult', 'admin'].includes(role);
}

export { SECTIONS, SUB_TABS };
